/**
 * The quiz question pool (plan docs/plans/lms-plan.md 8.1 "questions in a
 * second tab with the option grid and the feedback fields"; 5.2, 6.2 rules 1,
 * 4 and 5, Appendix E quiz.questions[], 18.11; WP-44; R21). Every question
 * names the section it tests and the objective it maps to (6.2 rule 1; the
 * objective_index column, 18.11), carries feedback on every option and an
 * explanation, and keeps its correct flags (the admin's detail carries them,
 * 8.1). Ordinals follow the order on screen (the save renumbers them).
 */
import { Plus } from 'lucide-react';
import { outlineBtnCls } from '../../../../ui/styles';
import type { CourseSection, Difficulty, QuestionKind } from '../../../content/types';
import { DIFFICULTIES, QUESTION_KINDS } from '../../../content/types';
import type { EditorQuestion } from '../../../api/catalog';
import { RowActions, SelectField, TextArea, TextInput } from './fields';
import { blankQuestion, moveItem, removeAt, replaceAt } from './model';
import { ChoiceOptionsEditor, optionsForKind, QUESTION_KIND_LABELS } from './SlideForm';
import { cardCls, hintCls, microLabelCls } from './styles';

const DIFFICULTY_LABELS: Record<Difficulty, string> = { recall: 'Recall', apply: 'Apply', analyze: 'Analyze' };

export interface QuestionsEditorProps {
  questions: EditorQuestion[];
  onChange: (questions: EditorQuestion[]) => void;
  sections: CourseSection[];
  objectives: string[];
  /** quiz_settings.draw_count, for the pool rule (6.2 rule 4: pool at least twice the draw). */
  drawCount: number | null;
  /** The scenario profile draws the whole pool (6.5). */
  scenario: boolean;
  /** Lint problems per question index. */
  errorCount: (index: number) => number;
}

export function QuestionsEditor({ questions, onChange, sections, objectives, drawCount, scenario, errorCount }: QuestionsEditorProps) {
  const sectionOptions = sections.map((s, i) => ({ value: s.id, label: `${i + 1}. ${s.title || s.id}` }));
  const objectiveOptions = objectives.map((o, i) => ({ value: String(i), label: `${i + 1}. ${o || '(objective not written yet)'}` }));
  const firstSection = sections[0]?.id ?? 's1';
  const poolNote = scenario
    ? `A scenario draws the whole pool: 3 to 6 questions (6.5). Pool: ${questions.length}.`
    : `Pool: ${questions.length} question${questions.length === 1 ? '' : 's'}; draw: ${drawCount ?? 'not set'}. The pool must be at least twice the draw (6.2 rule 4).`;

  return (
    <div className="space-y-4" data-testid="lms-questions-editor">
      <p className={hintCls} data-testid="lms-pool-note">
        {poolNote}
      </p>
      {questions.map((q, i) => {
        const count = errorCount(i);
        const set = (patch: Partial<EditorQuestion>) => onChange(replaceAt(questions, i, { ...q, ...patch }));
        return (
          <section key={i} className={`${cardCls} space-y-3`} data-testid={`lms-question-${i}`} aria-label={`Question ${i + 1}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className={microLabelCls}>{`Question ${i + 1}`}</span>
                {count > 0 ? (
                  <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800 dark:bg-red-900/40 dark:text-red-300">
                    {`${count} to fix`}
                  </span>
                ) : null}
              </div>
              <RowActions
                index={i}
                count={questions.length}
                label={`question ${i + 1}`}
                onMove={(to) => onChange(moveItem(questions, i, to))}
                onRemove={() => onChange(removeAt(questions, i))}
              />
            </div>
            <TextArea label="Question" value={q.stem} onChange={(stem) => set({ stem })} testId={`lms-question-stem-${i}`} rows={2} />
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <SelectField
                label="Answer type"
                value={q.kind}
                onChange={(kind) => set({ kind: kind as QuestionKind, options: optionsForKind(q.options, kind as QuestionKind) })}
                options={QUESTION_KINDS.map((k) => ({ value: k, label: QUESTION_KIND_LABELS[k] }))}
                testId={`lms-question-kind-${i}`}
              />
              <SelectField
                label="Section it tests"
                value={q.section_id}
                onChange={(section_id) => set({ section_id })}
                options={sectionOptions.length > 0 ? sectionOptions : [{ value: q.section_id, label: q.section_id }]}
                testId={`lms-question-section-${i}`}
              />
              <SelectField
                label="Objective it tests"
                value={q.objective_index === null || q.objective_index === undefined ? '' : String(q.objective_index)}
                onChange={(v) => set({ objective_index: v === '' ? null : Number(v) })}
                options={[{ value: '', label: 'Not mapped yet' }, ...objectiveOptions]}
                testId={`lms-question-objective-${i}`}
              />
              <SelectField
                label="Difficulty"
                value={q.difficulty}
                onChange={(difficulty) => set({ difficulty: difficulty as Difficulty })}
                options={DIFFICULTIES.map((d) => ({ value: d, label: DIFFICULTY_LABELS[d] }))}
              />
            </div>
            <ChoiceOptionsEditor
              options={q.options}
              kind={q.kind}
              onChange={(options) => set({ options })}
              testId={`lms-question-options-${i}`}
            />
            <TextArea
              label="Explanation (shown after answering, whatever the result)"
              value={q.explanation}
              onChange={(explanation) => set({ explanation })}
              rows={2}
            />
            <TextInput
              label="Source reference (which source backs the answer)"
              value={q.source_ref ?? ''}
              onChange={(source_ref) => set({ source_ref: source_ref || null })}
            />
          </section>
        );
      })}
      <button
        type="button"
        className={`${outlineBtnCls()} disabled:cursor-not-allowed disabled:opacity-50`}
        onClick={() => onChange([...questions, blankQuestion(firstSection, questions.length + 1)])}
        disabled={scenario && questions.length >= 6}
        data-testid="lms-add-question"
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        Add question
      </button>
    </div>
  );
}

export default QuestionsEditor;
